import React from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import {
  NavigationProvider,
  useNavigation,
} from "./contexts/NavigationContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { UpgradeProvider } from "./contexts/UpgradeContext";
import { TokenProvider } from "./contexts/TokenContext";
import { I18nProvider } from "./contexts/I18nContext";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { ToastProvider } from "./contexts/ToastContext";
import { Layout } from "./components/Layout";
import { BackgroundTaskNotifications } from "./components/BackgroundTaskNotifications";
import { UpgradeModal } from "./components/UpgradeModal";
import TokenPurchaseModal from "./components/TokenPurchaseModal";
import { Dashboard } from "./components/Dashboard";
// Login component commented out - using 3PM Auth for all authentication
// import { Login } from "./components/Login";
import { Admin } from "./components/Admin";
import { UserManagement } from "./components/UserManagement";
import { AcceptInvitation } from "./components/AcceptInvitation";
import { MagicLink } from "./components/MagicLink";
import { FirstTime2FASetup } from "./components/FirstTime2FASetup";
import { XeroCallback } from "./components/XeroCallback";
import { MYOBCallback } from "./components/MYOBCallback";
import { GmailCallback } from "./components/GmailCallback";
import { OutlookCallback } from "./components/OutlookCallback";
import { AccountsManagement } from "./components/admin/AccountsManagement";
import { Testing } from "./components/Testing";
import { Escrow } from "./components/Escrow";
import { ConstructionProjects } from "./components/construction/ConstructionProjects";
import { Contacts } from "./components/Contacts";
import {
  WalkthroughProvider,
  useWalkthrough,
} from "./contexts/WalkthroughContext";
import { Walkthrough } from "./components/common/Walkthrough";
import { EmailPreferenceCenter } from "./components/EmailPreferenceCenter";
import { SSOCallback } from "./components/SSOCallback";
import { Onboarding } from "./components/Onboarding";

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { currentPath } = useNavigation();
  const { activeWalkthrough, stopWalkthrough } = useWalkthrough();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Helper function to redirect to 3PM Auth login
  const redirectToSSOLogin = () => {
    // Check if we're in the middle of logging out
    const isLoggingOut = sessionStorage.getItem("isLoggingOut");
    if (isLoggingOut === "true") {
      // Don't redirect to login yet - logout process is handling it
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Signing out...</p>
          </div>
        </div>
      );
    }

    const returnUrl = `${window.location.origin}/auth/sso-callback`;
    window.location.href = `${apiUrl}/api/sso/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to authentication...</p>
        </div>
      </div>
    );
  };

  // Check current path first for public routes

  // Public route: SSO callback (both /auth-callback and /auth/sso-callback)
  // 3PM Auth may redirect to either URL depending on configuration
  if (currentPath.startsWith("/auth-callback") || currentPath.startsWith("/auth/sso-callback")) {
    return <SSOCallback />;
  }

  // Public route: Onboarding (after SSO - new user account setup)
  if (currentPath.startsWith("/auth/onboarding") || currentPath.startsWith("/auth/create-account")) {
    return <Onboarding />;
  }

  // Public route: Accept invitation (redirects to 3PM Auth)
  if (currentPath.startsWith("/auth/accept-invite")) {
    const urlParams = new URLSearchParams(window.location.search);
    const invitationId = urlParams.get("id");
    const type = urlParams.get("type"); // 'project' for project invitations

    if (invitationId) {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const returnUrl = `${window.location.origin}/auth/sso-callback`;
      const state = type === "project" ? `proj:${invitationId}` : invitationId;
      window.location.href = `${apiUrl}/api/sso/login?returnUrl=${encodeURIComponent(returnUrl)}&state=${state}`;
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Redirecting to authentication...</p>
          </div>
        </div>
      );
    } else {
      return <AcceptInvitation />;
    }
  }

  // Public route: Magic link (auto-login from invitation) - kept for backward compatibility
  if (currentPath.startsWith("/auth/magic-link")) {
    return <MagicLink />;
  }

  // Public route: Accept invitation (kept for backward compatibility)
  if (currentPath.startsWith("/auth/accept-invitation")) {
    return <AcceptInvitation />;
  }

  // First-time 2FA setup (requires authentication)
  // This should show as a modal overlay with the app in the background
  if (currentPath.startsWith("/auth/setup-2fa")) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }
    if (!isAuthenticated) {
      return redirectToSSOLogin();
    }
    // Render the app layout with the 2FA setup modal overlay
    // Don't render Dashboard content - just show the layout structure with 2FA modal
    return (
      <UpgradeProvider>
        <PermissionsProvider>
          <WebSocketProvider>
            <Layout>
              {/* Empty div to maintain layout structure without loading Dashboard */}
              <div className="flex-1" />
            </Layout>
            <BackgroundTaskNotifications />
            <UpgradeModal />
            {activeWalkthrough && (
              <Walkthrough
                config={activeWalkthrough}
                onClose={stopWalkthrough}
              />
            )}
            <FirstTime2FASetup />
          </WebSocketProvider>
        </PermissionsProvider>
      </UpgradeProvider>
    );
  }

  // Public route: Email Preference Center (token-based, no auth required)
  if (currentPath.startsWith("/email-preferences/")) {
    return <EmailPreferenceCenter />;
  }

  // OAuth callback routes (require authentication)
  if (currentPath.startsWith("/auth/xero/callback")) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }
    if (!isAuthenticated) {
      return redirectToSSOLogin();
    }
    return <XeroCallback />;
  }

  if (currentPath.startsWith("/auth/myob/callback")) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }
    if (!isAuthenticated) {
      return redirectToSSOLogin();
    }
    return <MYOBCallback />;
  }

  if (currentPath.startsWith("/auth/gmail/callback")) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }
    if (!isAuthenticated) {
      return redirectToSSOLogin();
    }
    return <GmailCallback />;
  }

  if (currentPath.startsWith("/auth/outlook/callback")) {
    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      );
    }
    if (!isAuthenticated) {
      return redirectToSSOLogin();
    }
    return <OutlookCallback />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to 3PM Auth login instead of showing Login component
    // Login component is commented out as we're using 3PM Auth for all authentication
    return redirectToSSOLogin();
  }

  // Render page content based on current path
  const renderPageContent = () => {
    if (currentPath === "/users") {
      return <UserManagement mode="page" />;
    }

    // Admin route - handle with or without query parameters
    if (currentPath.startsWith("/admin")) {
      return <Admin />;
    }

    // Escrow route - System Admin only
    if (currentPath.startsWith("/escrow")) {
      return <Escrow />;
    }

    // Construction Projects (Retentions & Escrow)
    if (currentPath.startsWith("/projects")) {
      return <ConstructionProjects />;
    }

    if (currentPath.startsWith("/contacts")) {
      return <Contacts mode="page" />;
    }

    if (currentPath === "/accounts") {
      return <AccountsManagement mode="page" />;
    }

    if (
      currentPath.startsWith("/accounts/") &&
      currentPath.split("/").length === 3
    ) {
      const accountId = currentPath.split("/")[2];
      return <AccountsManagement mode="page" initialAccountId={accountId} />;
    }

    if (currentPath === "/testing") {
      return <Testing />;
    }

    // Default to Dashboard for home and other paths
    return <Dashboard />;
  };

  // Single Layout wrapper - Header/Sidebar stay mounted across all navigations
  return (
    <ToastProvider>
      <FeatureFlagProvider>
        <UpgradeProvider>
          <WebSocketProvider>
            <TokenProvider>
              <PermissionsProvider>
                <NotificationProvider>
                  <Layout>{renderPageContent()}</Layout>
                  <BackgroundTaskNotifications />
                  <UpgradeModal />
                  <TokenPurchaseModal />
                  {activeWalkthrough && (
                    <Walkthrough config={activeWalkthrough} onClose={stopWalkthrough} />
                  )}
                </NotificationProvider>
              </PermissionsProvider>
            </TokenProvider>
          </WebSocketProvider>
        </UpgradeProvider>
      </FeatureFlagProvider>
    </ToastProvider>
  );
};

function App() {
  return (
    <BrandingProvider>
      <I18nProvider>
        <AuthProvider>
          <FeatureFlagProvider>
            <NavigationProvider>
              <WalkthroughProvider>
                <AppContent />
              </WalkthroughProvider>
            </NavigationProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </I18nProvider>
    </BrandingProvider>
  );
}

export default App;
